import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ConfigProvider, theme } from "antd";
import { useTranslation } from "react-i18next";
import { getLanguageVariant } from "helpers/core";
import dayjs from "dayjs";
import type { Locale } from "antd/lib/locale";
import "@fontsource/poppins";
import { CustomThemeConfig, getCustomTheme } from "../theme";
import { FeatureFlags } from "@/constants";
import axiosInstance from "@/services/api";
import {
  THIRTEEN_MONTHS_DAYS,
  getStoredItem,
  setStoredItem,
} from "@libertify.frontend/helpers";

// Statically import dayjs locales so they self-register with the same dayjs
// instance via side effect. Switching is then done by name (dayjs.locale("fr"))
// to avoid Vite's CJS interop wrapping the locale object's default export.
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/es";

// Vite glob imports for antd locales - analyzed at build time
const antdLocales = import.meta.glob<{ default: Locale }>(
  "../../node_modules/antd/locale/*.js"
);

type ColorModeContextType = {
  mode: string;
  setMode: (mode: string) => void;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const DEFAULT_COLOR_MODE = "light"; // light default theme forced, via MED-1316
  const [mode, setMode] = useState(DEFAULT_COLOR_MODE);

  // Fetch feature flags and apply dark mode if allowed
  useEffect(() => {
    axiosInstance
      .get<Record<string, boolean>>("/api/system/features")
      .then((res) => {
        const isDarkModeAllowed = res.data[FeatureFlags.DarkMode] === true;
        const colorModeFromLocalStorage = getStoredItem("colorMode");

        let newMode = "light";

        if (isDarkModeAllowed && colorModeFromLocalStorage === "dark") {
          setMode("dark");
          newMode = "dark";
        }

        setStoredItem("colorMode", newMode, THIRTEEN_MONTHS_DAYS);
      })
      .catch((error) => {
        console.error("Error fetching feature flags:", error);
      });
  }, []);

  const setColorMode = useCallback(() => {
    setMode((prev) => {
      const newMode = prev === "light" ? "dark" : "light";
      setStoredItem("colorMode", newMode, THIRTEEN_MONTHS_DAYS);
      return newMode;
    });
  }, []);

  const { darkAlgorithm, defaultAlgorithm } = theme;
  const colors = {
    primary: "#3BD4CA",
    white: mode === "dark" ? "#fff" : "#000",
    black: mode === "dark" ? "#000" : "#fff",
  };

  const customTheme: CustomThemeConfig = getCustomTheme(
    mode,
    defaultAlgorithm,
    darkAlgorithm,
    colors
  );
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState({ locale: "en" });

  const loadLocale = async (language: string) => {
    const lang = getLanguageVariant(language);
    const antdLang = lang.replace("-", "_");
    const dayjsLang = lang.split("-").shift() ?? "en";

    const antdKey = `../../node_modules/antd/locale/${antdLang}.js`;
    const antdLoader = antdLocales[antdKey];

    if (!antdLoader) {
      console.warn(
        `Locale not found: ${language} (antd: ${antdLang}, dayjs: ${dayjsLang})`
      );
      return { antdLocale: undefined, dayjsLang };
    }

    const antdModule = await antdLoader();

    return {
      antdLocale: antdModule.default,
      dayjsLang,
    };
  };

  useEffect(() => {
    (async () => {
      if (locale.locale !== i18n.language || dayjs.locale() !== i18n.language) {
        const { antdLocale, dayjsLang } = await loadLocale(i18n.language);
        if (antdLocale) setLocale(antdLocale);
        dayjs.locale(dayjsLang);
      }
    })().catch((error) => {
      console.error("Error loading locale:", error);
    });
  }, [i18n.language, locale.locale]);

  const contextValue = useMemo(
    () => ({ mode, setMode: setColorMode }),
    [mode, setColorMode]
  );
  return (
    <ColorModeContext.Provider value={contextValue}>
      <ConfigProvider locale={locale} theme={customTheme}>
        {children}
      </ConfigProvider>
    </ColorModeContext.Provider>
  );
};
